// How long the "Added to cart" line stays once it is shown (showAdded)
const ADDED_LINE_MS = 5000;
// Its fade and collapse (hideAdded); matches the transition in snippets/cart-drawer.liquid
const ADDED_LINE_FADE_MS = 200;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.trackPointer();
  }

  // Whether a finger or button is down that went down inside the drawer. The "Added to cart" line does not go
  // away under it (holdAdded); a pointer that lifts anywhere, or is cancelled, ends it.
  trackPointer() {
    this.pointerDown = false;
    this.addEventListener('pointerdown', () => {
      this.pointerDown = true;
    });
    const lift = () => {
      if (!this.pointerDown) return;
      this.pointerDown = false;
      const then = this.afterPointerUp;
      this.afterPointerUp = null;
      if (then) then();
    };
    document.addEventListener('pointerup', lift, true);
    document.addEventListener('pointercancel', lift, true);
    window.addEventListener('blur', lift);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
      // opened by hand (the header cart icon), not by an add
      this.clearAdded();
    }
    // cart.js refreshes the items and footer on its own after an add; whatever it left, show what the lines say
    this.syncEmptyState();
    this.updateScrollHint();
    this.watchScroll();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setTimeout(() => {
      this.classList.add('animate', 'active');
      if (reduceMotion) this.trapFocusWhenVisible();
      // the "Added to cart" sentence is said from here, not from the repaint: it needs a dialog that is on screen
      this.announceAdded();
    });

    if (!reduceMotion) this.addEventListener('transitionend', () => this.trapDrawerFocus(), { once: true });

    document.body.classList.add('overflow-hidden');
  }

  // Runs `then` on the first frame where the dialog is no longer visibility:hidden (gives up waiting after 10
  // frames), and not at all once the drawer has closed. Each level of the tree takes the drawer's visibility a
  // frame after its parent, and a slow device takes longer still, so neither a focus() nor a live-region write on
  // the next frame can count on it.
  whenVisible(then, framesLeft = 10) {
    requestAnimationFrame(() => {
      if (!this.classList.contains('active')) return;
      const dialog = this.querySelector('.drawer__inner');
      if (dialog && framesLeft > 0 && getComputedStyle(dialog).visibility === 'hidden') {
        this.whenVisible(then, framesLeft - 1);
      } else {
        then();
      }
    });
  }

  // Reduced motion: transitionend fires before the dialog is visible, so a focus() on the next frame lands nowhere.
  trapFocusWhenVisible() {
    this.whenVisible(() => this.trapDrawerFocus());
  }

  trapDrawerFocus() {
    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : document.getElementById('CartDrawer');
    const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
    trapFocus(containerToTrapFocusOn, focusElement);
  }

  close() {
    this.classList.remove('active');
    this.stopWatchingScroll();
    // the line itself goes once the panel is out of sight (clearAddedWhenClosed)
    clearTimeout(this.addedTimer);
    this.clearAddedWhenClosed();
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // Repaints the drawer from a /cart/add response and opens it. Everything is worked out before the page
  // is touched, so a response with no `sections` (or none holding the drawer) throws with the drawer as it was.
  renderContents(parsedState) {
    const sections = parsedState.sections || {};
    const replacements = this.getSectionsToRender()
      .map((section) => ({
        section,
        element: section.selector ? document.querySelector(section.selector) : document.getElementById(section.id),
      }))
      .filter(({ element }) => element)
      .map(({ section, element }) => ({
        element,
        html: this.getSectionInnerHTML(sections[section.id], section.selector),
      }));

    this.productId = parsedState.id;
    replacements.forEach(({ element, html }) => {
      element.innerHTML = html;
    });
    this.syncEmptyState();
    this.updateScrollHint();
    // said by open() once the dialog is visible (announceAdded)
    this.showAdded(parsedState.product_title);

    return new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
          if (this.classList.contains('active')) this.settleOpen();
          else this.open();
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    });
  }

  // A repaint of a drawer that is open already (a second Add lands while it is showing): the controls behind the
  // focus are new, and nothing transitions. open() would wait for a transitionend that never comes, leaving its
  // listener to pull focus back while the drawer closes, and focus on body in the meantime. So this does what open()
  // does for the markup, and traps focus at once, on the heading (not on the dialog box, as open() does) when the
  // drawer has one on show.
  settleOpen() {
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.announceAdded();

    const empty = this.classList.contains('is-empty');
    const heading = this.querySelector('.drawer__heading');
    const target = heading && heading.getClientRects().length ? heading : this.querySelector('.drawer__inner');
    trapFocus(
      empty ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer'),
      target,
      { preventScroll: true }
    );
  }

  // The cart's emptiness is drawn in three places that are not repainted together (the is-empty class,
  // the .drawer__inner-empty block, the Checkout button), so derive all three from the lines in the drawer.
  syncEmptyState() {
    const hasItems = Boolean(this.querySelector('.cart-item'));
    this.classList.toggle('is-empty', !hasItems);

    const emptyState = this.querySelector('.drawer__inner-empty');
    if (emptyState && hasItems) emptyState.remove();

    // Checkout is held disabled while a cart request is pending (window.pmCartLock); the unlock frees it
    const checkout = this.querySelector('#CartDrawer-Checkout');
    if (checkout && !checkout.hasAttribute('data-pm-locked')) checkout.disabled = !hasItems;

    // A repaint draws its controls unlocked; while a request holds the lock they are locked like the rest
    if (window.pmCartLock) window.pmCartLock.apply();
  }

  // "Added to cart: <name>" from data-added; the name is the title before its first " \u2014 " (as
  // pm-product-name.liquid). Empty without a title. The one place this sentence is built (product-form.js too).
  addedText(productTitle) {
    const title = String(productTitle || '').trim();
    if (!title) return '';
    const name = title.split(' \u2014 ')[0].trim() || title;
    return (this.dataset.added || 'Added to cart: __PRODUCT__').replace('__PRODUCT__', () => name);
  }

  // Status line at the top of the drawer body after an add (product form, sticky bar, "Pairs well").
  // Gone again after ADDED_LINE_MS (holdAdded), when a quantity or remove update starts (hideAdded, from
  // CartDrawerItems), once the drawer has closed (clearAddedWhenClosed) or when it is opened by hand.
  showAdded(productTitle) {
    this.clearAdded();

    const body = this.querySelector('.pm-drawer__body');
    const template = this.querySelector('#CartDrawer-AddedTemplate');
    const message = this.addedText(productTitle);
    if (!body || !template || !message) return;

    this.addedMessage = message;
    const line = template.content.firstElementChild.cloneNode(true);
    line.querySelector('span').textContent = message;
    body.prepend(line);
    this.addedLine = line;
    this.addedTimer = setTimeout(() => this.holdAdded(line), ADDED_LINE_MS);
    this.updateScrollHint();
  }

  clearAdded() {
    this.addedMessage = '';
    this.addedLine = null;
    this.afterPointerUp = null;
    clearTimeout(this.addedTimer);
    clearTimeout(this.addedHideTimer);
    clearTimeout(this.announceTimer);
    const status = this.querySelector('#CartDrawer-AddedStatus');
    if (status) status.textContent = '';
    const line = this.querySelector('.pm-drawer__added');
    if (line) line.remove();
  }

  // The time is up. Not under a pointer that went down in the drawer: wait for it to lift, then hide the line.
  // (Nothing in the line can take focus, so focus never holds it.)
  holdAdded(line) {
    if (this.addedLine !== line) return;

    if (this.pointerDown) {
      this.afterPointerUp = () => this.holdAdded(line);
    } else {
      this.hideAdded();
    }
  }

  // Fades the line out and collapses its height, then removes it (clearAdded). With reduced motion it is
  // removed at once.
  hideAdded() {
    clearTimeout(this.addedTimer);
    this.afterPointerUp = null;
    const line = this.querySelector('.pm-drawer__added');
    if (!line || line.classList.contains('pm-drawer__added--out')) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.clearAdded();
      return;
    }

    // a transition needs a number to start from
    line.style.height = `${line.offsetHeight}px`;
    line.getBoundingClientRect();
    line.classList.add('pm-drawer__added--out');
    clearTimeout(this.addedHideTimer);
    // unless a newer line has taken its place by then
    this.addedHideTimer = setTimeout(() => {
      if (this.addedLine === line) this.clearAdded();
    }, ADDED_LINE_FADE_MS + 20);
  }

  // Closing removes the line only once the panel has slid out of sight (removing it earlier pulls the
  // content up while it is still on screen), and only if the drawer is still closed by then.
  clearAddedWhenClosed() {
    if (this.cancelClear) this.cancelClear();

    const timer = setTimeout(() => finish(), 500);
    const cancel = () => {
      clearTimeout(timer);
      this.removeEventListener('transitionend', onSlideEnd);
      this.cancelClear = null;
    };
    const finish = () => {
      cancel();
      if (!this.classList.contains('active')) this.clearAdded();
    };
    const onSlideEnd = (event) => {
      if (event.propertyName === 'transform' && event.target.classList.contains('drawer__inner')) finish();
    };
    this.cancelClear = cancel;
    this.addEventListener('transitionend', onSlideEnd);
  }

  // Says the status line's sentence through the live region inside the dialog, 100 ms after the dialog is
  // visible (open() calls it once .active is on; "Pairs well" calls it with the drawer already open).
  // True when there is a sentence and a region to say it in.
  announceAdded() {
    clearTimeout(this.announceTimer);
    const region = this.querySelector('#CartDrawer-AddedStatus');
    if (!this.addedMessage || !region) return false;

    region.textContent = '';
    this.whenVisible(() => {
      clearTimeout(this.announceTimer);
      // a region is only announced when its text changes after it is in the DOM
      this.announceTimer = setTimeout(() => {
        // looked up again, and only while the line is there: a repaint in between replaces both
        const current = this.querySelector('#CartDrawer-AddedStatus');
        if (current && this.addedMessage && this.querySelector('.pm-drawer__added')) {
          current.textContent = this.addedMessage;
        }
      }, 100);
    });
    return true;
  }

  // On short phones the footer fades the bottom of the body while there is more to scroll to (CSS in
  // snippets/cart-drawer.liquid). Repaints replace the body, so listen from here, only while open.
  watchScroll() {
    if (this.stopWatching) return;

    let queued = false;
    let frame;
    const queue = () => {
      if (queued) return;
      queued = true;
      frame = requestAnimationFrame(() => {
        queued = false;
        this.updateScrollHint();
      });
    };
    // content arriving late ("Pairs well" cards) changes how far the body can scroll without a scroll or resize
    const observer = new MutationObserver(queue);

    this.addEventListener('scroll', queue, { capture: true, passive: true });
    window.addEventListener('resize', queue, { passive: true });
    observer.observe(this, { childList: true, subtree: true });

    this.stopWatching = () => {
      cancelAnimationFrame(frame);
      this.removeEventListener('scroll', queue, { capture: true });
      window.removeEventListener('resize', queue);
      observer.disconnect();
      this.stopWatching = null;
    };
  }

  stopWatchingScroll() {
    if (this.stopWatching) this.stopWatching();
  }

  updateScrollHint() {
    const body = this.querySelector('.pm-drawer__body');
    // a few pixels of overflow (3 px at 768x1024) are not worth the fade
    this.classList.toggle('pm-drawer--more', Boolean(body) && body.scrollHeight - body.scrollTop - body.clientHeight > 8);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const section = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (!section) throw new Error(`cart-drawer: nothing matches "${selector}" in the section response`);
    return section.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  // The drawer's loading state begins here, and the "Added to cart" line has done its job by then
  enableLoading(line) {
    super.enableLoading(line);
    const drawer = this.closest('cart-drawer');
    if (drawer && typeof drawer.hideAdded === 'function') drawer.hideAdded();
  }

  // Replaces everything inside .drawer__inner, the scroller with it: the lines of a quantity change, a Remove and the
  // resync of a failed change or Add are drawn where the shopper had scrolled to (CartItems.keepingDrawerScroll)
  paintSections(sections, empty) {
    this.keepingDrawerScroll(() => super.paintSections(sections, empty));
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
